import React, { useState, useEffect } from 'react'
import {
  Database, Shield, Users, Settings, Activity, TrendingUp,
  AlertCircle, CheckCircle, Clock, UserPlus, Menu, X, ChevronDown, ChevronRight, LogOut
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { DemoRequestsManager } from '@/components/admin/platform/DemoRequestsManager'
import { SignupRequestsManager } from '@/components/admin/platform/SignupRequestsManager'
import { PlatformFamiliesManager } from '@/components/admin/platform/PlatformFamiliesManager'
import { DebugSettingsManager } from '@/components/admin/platform/DebugSettingsManager'

export const PlatformAdminDashboard: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [stats, setStats] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login')
    toast.success('Logged out successfully')
  }

  // Check if user is platform admin
  useEffect(() => {
    if (!user?.is_platform_admin) {
      toast.error('Platform admin access required')
      navigate('/dashboard')
    }
  }, [user, navigate])

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'families', label: 'Families', icon: Users },
    { id: 'signup-requests', label: 'Signup Requests', icon: UserPlus },
    { id: 'demo-requests', label: 'Demo Requests', icon: Clock },
    { id: 'debug-settings', label: 'Debug Settings', icon: Settings },
  ]

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Platform Overview</h2>
              <p className="text-gray-600">System-wide metrics and status</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Families</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total_families || 0}</p>
                  </div>
                  <Users className="h-12 w-12 text-blue-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Users</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.active_users || 0}</p>
                  </div>
                  <Activity className="h-12 w-12 text-green-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Requests</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.pending_requests || 0}</p>
                  </div>
                  <Clock className="h-12 w-12 text-yellow-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">System Health</p>
                    <p className="text-3xl font-bold text-green-600">Good</p>
                  </div>
                  <CheckCircle className="h-12 w-12 text-green-500" />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setActiveSection('signup-requests')}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <UserPlus className="h-6 w-6 text-blue-500" />
                    <span className="font-medium">Review Signup Requests</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>

                <button
                  onClick={() => setActiveSection('demo-requests')}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Clock className="h-6 w-6 text-yellow-500" />
                    <span className="font-medium">Review Demo Requests</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>

                <button
                  onClick={() => setActiveSection('families')}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Users className="h-6 w-6 text-purple-500" />
                    <span className="font-medium">Manage Families</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>

                <button
                  onClick={() => setActiveSection('debug-settings')}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <Settings className="h-6 w-6 text-gray-500" />
                    <span className="font-medium">Debug Settings</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        )

      case 'families':
        return <PlatformFamiliesManager />

      case 'signup-requests':
        return <SignupRequestsManager />

      case 'demo-requests':
        return <DemoRequestsManager />

      case 'debug-settings':
        return <DebugSettingsManager />

      default:
        return <div>Section not found</div>
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8 text-red-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
                <p className="text-sm text-gray-600">admin.housepoints.ai</p>
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Sidebar Navigation */}
          <aside className={`
            lg:col-span-3 mb-8 lg:mb-0
            ${mobileMenuOpen ? 'block' : 'hidden lg:block'}
          `}>
            <nav className="bg-white rounded-lg shadow p-4 space-y-1">
              {menuItems.map(item => {
                const Icon = item.icon
                const isActive = activeSection === item.id

                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveSection(item.id)
                      setMobileMenuOpen(false)
                    }}
                    className={`
                      w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                      ${isActive
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                      }
                    `}
                  >
                    <Icon className={`h-5 w-5 ${isActive ? 'text-blue-700' : 'text-gray-400'}`} />
                    <span>{item.label}</span>
                  </button>
                )
              })}
            </nav>

            {/* User Info */}
            <div className="mt-4 bg-white rounded-lg shadow p-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.display_name}
                  </p>
                  <p className="text-xs text-gray-500">Platform Admin</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9">
            {renderContent()}
          </main>
        </div>
      </div>
    </div>
  )
}
